import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";
import {
  saveSkills,
  loadSkills,
  saveCerts,
  loadCerts,
  saveProofs,
  loadProofs,
  uploadProofFile,
  setEmailMuteAll,
  loadEmailPrefs,
  setPublicProfile,
  loadPublicProfile,
  removePublicProfile,
} from "../../lib/firebase";
import { useAuth } from "../../context/AuthContext";

// ── Styled Components ──────────────────────────────────────────────

const SectionStyled = styled.section`
  padding: 96px 0 48px;
  background: var(--nd-black);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background-image: radial-gradient(circle, var(--nd-border) 0.5px, transparent 0.5px);
    background-size: 12px 12px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const HeroBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  display: block;
  margin-bottom: 24px;
`;

const HeroTitle = styled.h1`
  font-family: 'Doto', 'Space Mono', monospace;
  font-size: 64px;
  font-weight: 700;
  color: var(--nd-text-display);
  letter-spacing: -0.03em;
  line-height: 1.0;
  margin-bottom: 24px;
  @media (max-width: 768px) { font-size: 40px; }
`;

const HeroSubtitle = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 300;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 560px;
  margin-bottom: 48px;
`;

const Section = styled.section`
  padding: 96px 0;
  background: var(--nd-black);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 8px;
`;

const SectionDesc = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
  max-width: 600px;
  margin-bottom: 48px;
`;

// ── Timeline ───────────────────────────────────────────────────────

const TimelineWrapper = styled.div`
  overflow-x: auto;
  padding-bottom: 16px;
  margin-bottom: 48px;
  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-track { background: var(--nd-surface); }
  &::-webkit-scrollbar-thumb { background: var(--nd-border-visible); border-radius: 2px; }
`;

const LaneContainer = styled.div` min-width: 900px; `;

const Lane = styled.div` margin-bottom: 24px; `;

const LaneName = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
`;

const LaneNodes = styled.div` display: flex; align-items: center; gap: 0; `;

const Arrow = styled.div`
  width: 40px;
  height: 1px;
  background: var(--nd-border-visible);
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '>';
    position: absolute;
    right: -2px;
    top: -8px;
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    color: var(--nd-text-disabled);
  }
`;

const NodeCard = styled.button`
  background: ${({ active }) => active ? "var(--nd-surface)" : "transparent"};
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border)"};
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
  min-width: 140px;
  outline: none;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-text-secondary); }
`;

const NodeTitle = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 2px;
`;

const NodeCode = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--nd-text-disabled);
  text-transform: uppercase;
`;

// ── Detail Panel ───────────────────────────────────────────────────

const DetailPanel = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 32px;
  margin-top: 16px;
  margin-bottom: 48px;
`;

const DetailTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 4px;
`;

const DetailLane = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const DetailBlock = styled.div``;

const DetailBlockLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 8px;
`;

const DetailBlockContent = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-secondary);
  line-height: 1.6;
`;

// ── Skills & Certifications ────────────────────────────────────────

const SkillRow = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  border-bottom: 1px solid var(--nd-border);
  &:last-child { border-bottom: none; }
`;

const SkillTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  @media (max-width: 576px) { flex-direction: column; align-items: flex-start; }
`;

const SkillRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProofToggle = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: transparent;
  border: 1px solid var(--nd-border-visible);
  color: var(--nd-text-secondary);
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  outline: none;
  &:hover { color: var(--nd-text-display); border-color: var(--nd-text-display); }
`;

const ProofPanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: var(--nd-surface-raised);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  display: ${({ open }) => (open ? "block" : "none")};
`;

const ProofTabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
`;

const ProofTab = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => (active ? "var(--nd-text-display)" : "var(--nd-border-visible)")};
  background: ${({ active }) => (active ? "var(--nd-text-display)" : "transparent")};
  color: ${({ active }) => (active ? "var(--nd-black)" : "var(--nd-text-disabled)")};
  cursor: pointer;
  outline: none;
`;

const ProofInput = styled.input`
  width: 100%;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--nd-border-visible);
  color: var(--nd-text-primary);
  padding: 6px 4px;
  outline: none;
  &:focus { border-color: var(--nd-text-display); }
`;

const ProofRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const ProofSaveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 14px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
`;

const ProofExisting = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-interactive) !important;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const ProofBadge = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-success);
  margin-left: 8px;
`;

const SignInProofCTA = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 8px 18px;
  border-radius: 999px;
  border: none;
  background: var(--nd-text-display);
  color: var(--nd-black);
  cursor: pointer;
  outline: none;
  &:hover { opacity: 0.85; }
`;

const SkillName = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
  min-width: 200px;
`;

const LevelButtons = styled.div` display: flex; gap: 4px; `;

const LevelBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ active }) => active ? "var(--nd-text-display)" : "var(--nd-border)"};
  background: ${({ active }) => active ? "var(--nd-text-display)" : "transparent"};
  color: ${({ active }) => active ? "var(--nd-black)" : "var(--nd-text-disabled)"};
  cursor: pointer;
  outline: none;
  transition: all 200ms ease;
  &:hover { border-color: var(--nd-text-secondary); }
`;

const MentorshipCallout = styled.div`
  border-left: 3px solid var(--nd-accent);
  background: var(--nd-surface);
  padding: 16px 20px;
  margin-bottom: 24px;
  border-radius: 0 8px 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MentorshipLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-accent);
`;

const MentorshipText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  line-height: 1.5;
  margin: 0;
`;

const CertCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const CertRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const CertActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CertName = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-display);
`;

const CertIssuer = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  color: var(--nd-text-disabled);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const CertStatus = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ status }) =>
    status === "achieved" ? "rgba(74,158,92,0.4)" :
    status === "in_progress" ? "rgba(212,168,67,0.4)" :
    "var(--nd-border-visible)"};
  background: ${({ status }) =>
    status === "achieved" ? "rgba(74,158,92,0.15)" :
    status === "in_progress" ? "rgba(212,168,67,0.15)" :
    "transparent"};
  color: ${({ status }) =>
    status === "achieved" ? "var(--nd-success)" :
    status === "in_progress" ? "var(--nd-warning)" :
    "var(--nd-text-disabled)"};
  cursor: ${({ disabled }) => disabled ? "default" : "pointer"};
  outline: none;
  &:disabled { opacity: 0.7; }
  &:hover:not(:disabled) {
    border-color: var(--nd-text-display);
  }
`;

// ── Team Career Cards ──────────────────────────────────────────────

const TeamCareerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
`;

const TeamCard = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 24px;
  transition: border-color 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover { border-color: var(--nd-border-visible); }
`;

const TeamName = styled.h4`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 4px;
`;

const TeamRole = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
`;

const TeamPath = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const PathNode = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ current }) => current ? "var(--nd-text-display)" : "var(--nd-border)"};
  background: ${({ current }) => current ? "var(--nd-text-display)" : "transparent"};
  color: ${({ current }) => current ? "var(--nd-black)" : "var(--nd-text-disabled)"};
`;

const PathArrow = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: var(--nd-text-disabled);
`;

const TrackSwitch = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px dashed var(--nd-warning);
  color: var(--nd-warning);
  background: rgba(212,168,67,0.1);
`;

const TeamCerts = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const MiniCert = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  color: var(--nd-text-disabled);
  background: rgba(255,255,255,0.03);
  padding: 2px 8px;
  border-radius: 4px;
`;

// ── Callout boxes ──────────────────────────────────────────────────

const GrowthBox = styled.div`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border);
  border-radius: 12px;
  padding: 48px;
  margin-top: 48px;
  @media (max-width: 768px) { padding: 32px 24px; }
`;

const GrowthTitle = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 24px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin-bottom: 16px;
`;

const GrowthText = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  color: var(--nd-text-secondary);
  line-height: 1.7;
  max-width: 640px;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

const Honest = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  color: var(--nd-text-disabled);
  font-style: italic;
`;

// ── Data ───────────────────────────────────────────────────────────

const lanes = [
  {
    name: "DevOps",
    nodes: [
      { title: "Junior DevOps", code: "JT", requirements: "Basic Linux, scripting, CI/CD concepts, Git.", skills: "Docker basics, one cloud provider, basic networking.", experience: "0-2 years", salary: "$$$" },
      { title: "DevOps Engineer", code: "TL / ST / EN", requirements: "CI/CD pipelines, IaC (Terraform), containers, monitoring.", skills: "Kubernetes, Helm, Prometheus/Grafana, incident response.", experience: "2-5 years", salary: "$$$$" },
      { title: "Senior DevOps", code: "SE / CL", requirements: "Architecture decisions, cost optimization, security, mentoring.", skills: "Multi-cloud, service mesh, GitOps, chaos engineering.", experience: "5-8 years", salary: "$$$$$" },
      { title: "Consultant", code: "SC", requirements: "Client advisory, roadmaps, team assessment.", skills: "Stakeholder management, platform strategy, migration planning.", experience: "8-12 years", salary: "$$$$$$" },
      { title: "Principal", code: "ML", requirements: "Org-wide strategy, engineering culture, cross-team alignment.", skills: "Executive communication, vendor negotiations, thought leadership.", experience: "12+ years", salary: "$$$$$$$" },
    ],
  },
  {
    name: "SRE",
    nodes: [
      { title: "Junior SRE", code: "JT", requirements: "Monitoring basics, incident triage, runbook following.", skills: "Linux, scripting, SLIs/SLOs, log analysis.", experience: "0-2 years", salary: "$$$" },
      { title: "SRE", code: "EN", requirements: "Incident management, toil reduction, SLO definition.", skills: "Observability stack, automation, distributed debugging.", experience: "2-5 years", salary: "$$$$" },
      { title: "Senior SRE", code: "SE", requirements: "Reliability architecture, error budgets, cross-team reviews.", skills: "Chaos engineering, capacity modeling, production readiness.", experience: "5-8 years", salary: "$$$$$" },
      { title: "Staff SRE", code: "ST", requirements: "Org-wide reliability strategy, incident commander training.", skills: "Executive reporting, multi-region DR, vendor SLA negotiation.", experience: "8+ years", salary: "$$$$$$" },
    ],
  },
  {
    name: "Platform",
    nodes: [
      { title: "Junior Platform", code: "JT", requirements: "API development, developer workflows, documentation.", skills: "One programming language, REST APIs, basic K8s, CI/CD.", experience: "0-2 years", salary: "$$$" },
      { title: "Platform Engineer", code: "EN", requirements: "Internal tooling, developer experience, self-service features.", skills: "Backstage/Port, custom operators, golden paths.", experience: "2-5 years", salary: "$$$$" },
      { title: "Senior Platform", code: "SE", requirements: "Platform architecture, adoption metrics, API design.", skills: "Platform-as-product, Kubernetes operators, policy engines.", experience: "5-8 years", salary: "$$$$$" },
      { title: "Staff Platform", code: "ST", requirements: "Platform strategy, build-vs-buy, engineering productivity.", skills: "Technical product management, developer surveys, cost analysis.", experience: "8+ years", salary: "$$$$$$" },
    ],
  },
];

const certifications = [
  { name: "AWS Solutions Architect Associate", issuer: "AWS", achieved: true },
  { name: "AWS Solutions Architect Professional", issuer: "AWS", achieved: true },
  { name: "Certified Kubernetes Administrator (CKA)", issuer: "CNCF", achieved: true },
  { name: "Certified Kubernetes Security (CKS)", issuer: "CNCF", achieved: false },
  { name: "HashiCorp Terraform Associate", issuer: "HASHICORP", achieved: true },
  { name: "Google Cloud Professional Architect", issuer: "GCP", achieved: false },
  { name: "Linux Foundation SRE", issuer: "LINUX FOUND.", achieved: false },
  { name: "AWS DevOps Professional", issuer: "AWS", achieved: false },
];

const teamMembers = [
  {
    name: "Fabio Palumbo",
    role: "Founder & DevOps Architect",
    currentGrade: "SC",
    track: "DevOps",
    path: [
      { grade: "JT", track: "DevOps" },
      { grade: "EN", track: "DevOps" },
      { grade: "SE", track: "DevOps" },
      { grade: "SE", track: "SRE", switched: true },
      { grade: "SC", track: "DevOps", switched: true },
    ],
    certs: ["AWS SA Pro", "CKA", "Terraform"],
    note: "Jumped from DevOps to SRE at senior level, then back to DevOps as consultant. Kept the SRE skills, gained the breadth.",
  },
  {
    name: "Your Next Hire",
    role: "Junior DevOps Engineer",
    currentGrade: "JT",
    track: "DevOps",
    path: [
      { grade: "JT", track: "DevOps" },
    ],
    certs: [],
    note: "Starts here. We'll help them figure out the rest.",
  },
];

const skillsInventory = [
  "Linux & Networking", "Docker & Containers", "Kubernetes",
  "CI/CD Pipelines", "Infrastructure as Code", "Cloud Platforms (AWS/GCP/Azure)",
  "Monitoring & Observability", "Security & Compliance", "Scripting (Bash/Python)",
  "Incident Management", "Architecture & Design", "Technical Communication",
];

const levels = ["Learning", "Competent", "Expert"];
const STORAGE_KEY = "dioscuro-career-skills";
const CERTS_STORAGE_KEY = "dioscuro-career-certs";
const PROOFS_STORAGE_KEY = "dioscuro-career-proofs";

// ── Component ──────────────────────────────────────────────────────

const AuthBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--nd-border);
`;

const AuthLabel = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
`;

const AuthButton = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid var(--nd-border-visible);
  background: transparent;
  color: var(--nd-text-primary);
  cursor: pointer;
  outline: none;
  transition: border-color 200ms ease;
  &:hover { border-color: var(--nd-text-primary); }
`;

const SaveStatus = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-success);
  margin-left: 8px;
`;

const CareerPath = () => {
  const { user, signIn, signOut } = useAuth();
  const [activeNode, setActiveNode] = useState(null);
  const [saveMsg, setSaveMsg] = useState("");
  const [emailMuted, setEmailMuted] = useState(false);
  const [skills, setSkills] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [certs, setCerts] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(CERTS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [openProof, setOpenProof] = useState(null);
  const [proofTab, setProofTab] = useState("link");
  const [proofUrl, setProofUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const [proofs, setProofs] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const saved = localStorage.getItem(PROOFS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [openCertProof, setOpenCertProof] = useState(null);
  const [certProofTab, setCertProofTab] = useState("link");
  const [certProofUrl, setCertProofUrl] = useState("");
  const [certUploading, setCertUploading] = useState(false);
  const [profilePublic, setProfilePublic] = useState(false);
  const [copyHint, setCopyHint] = useState("");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    loadSkills(user.uid).then((remote) => {
      if (!cancelled && remote) setSkills(remote);
    });
    loadCerts(user.uid).then((remoteCerts) => {
      if (!cancelled && remoteCerts) setCerts(remoteCerts);
    });
    loadProofs(user.uid).then((remoteProofs) => {
      if (!cancelled && remoteProofs) setProofs(remoteProofs);
    });
    loadEmailPrefs(user.uid).then((prefs) => {
      if (!cancelled && prefs && typeof prefs.muteAll === "boolean") setEmailMuted(prefs.muteAll);
    });
    loadPublicProfile(user.uid).then((p) => {
      if (!cancelled && p) setProfilePublic(true);
    });
    return () => { cancelled = true; };
  }, [user]);

  const handleToggleEmailMute = async () => {
    if (!user) return;
    const next = !emailMuted;
    setEmailMuted(next);
    try {
      await setEmailMuteAll(user.uid, next);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[email-prefs] toggle failed:", err);
      setEmailMuted(!next);
    }
  };

  // Keep publicProfiles/{uid} in sync when toggle is on.
  // Debounced 400ms so rapid edits (typing in proof title, clicking
  // through skill levels) coalesce into one Firestore write.
  useEffect(() => {
    if (!user || !profilePublic) return;
    const t = setTimeout(() => {
      setPublicProfile(user.uid, {
        uid: user.uid,
        displayName: user.displayName || "Member",
        photoURL: user.photoURL || null,
        assessments: skills,
        certs,
        proofs,
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [profilePublic, skills, certs, proofs, user]);

  const handleTogglePublic = async () => {
    if (!user) return;
    if (profilePublic) {
      try {
        await removePublicProfile(user.uid);
        setProfilePublic(false);
      } catch (err) {
        console.error("[profile] off failed:", err);
      }
    } else {
      try {
        await setPublicProfile(user.uid, {
          uid: user.uid,
          displayName: user.displayName || "Member",
          photoURL: user.photoURL || null,
          assessments: skills,
          certs,
          proofs,
        });
        setProfilePublic(true);
      } catch (err) {
        console.error("[profile] on failed:", err);
      }
    }
  };

  const handleCopyShareLink = async () => {
    if (!user || typeof window === "undefined") return;
    const url = `${window.location.origin}/p/?uid=${user.uid}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopyHint("COPIED");
      setTimeout(() => setCopyHint(""), 1500);
    } catch (err) {
      console.error("[profile] copy failed:", err);
    }
  };

  // Save to localStorage always, Firebase when logged in
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(skills)); } catch {}
    if (user && Object.keys(skills).length > 0) {
      saveSkills(user.uid, skills).then(() => {
        setSaveMsg("SAVED");
        setTimeout(() => setSaveMsg(""), 2000);
      }).catch(() => {});
    }
  }, [skills, user]);

  useEffect(() => {
    try { localStorage.setItem(CERTS_STORAGE_KEY, JSON.stringify(certs)); } catch {}
    if (user && Object.keys(certs).length > 0) {
      saveCerts(user.uid, certs).catch(() => {});
    }
  }, [certs, user]);

  useEffect(() => {
    try { localStorage.setItem(PROOFS_STORAGE_KEY, JSON.stringify(proofs)); } catch {}
    if (user && Object.keys(proofs).length > 0) {
      saveProofs(user.uid, proofs).catch(() => {});
    }
  }, [proofs, user]);

  const handleProofToggle = (skill) => {
    if (openProof === skill) {
      setOpenProof(null);
    } else {
      setOpenProof(skill);
      setProofTab("link");
      setProofUrl(certs[skill]?.type === "link" ? certs[skill].url : "");
    }
  };

  const handleSaveLink = (skill) => {
    if (!proofUrl) return;
    setCerts((prev) => ({ ...prev, [skill]: { type: "link", url: proofUrl, name: proofUrl } }));
    setOpenProof(null);
  };

  const handleUploadFile = async (skill, file) => {
    if (!file) return;
    if (!user) return;
    setUploading(true);
    try {
      const result = await uploadProofFile(user.uid, skill, file);
      if (result) {
        setCerts((prev) => ({
          ...prev,
          [skill]: { type: "file", url: result.url, name: result.name, path: result.path },
        }));
        setOpenProof(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[proof] upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveCert = (skill) => {
    setCerts((prev) => {
      const next = { ...prev };
      delete next[skill];
      return next;
    });
  };

  // ── Certifications-section handlers ─────────────────────────────
  const CERT_STATUS_CYCLE = ["to_be_started", "in_progress", "achieved"];
  const CERT_STATUS_LABEL = {
    to_be_started: "TO BE STARTED",
    in_progress: "IN PROGRESS",
    achieved: "ACHIEVED",
  };

  const certStatus = (cert) => {
    const override = proofs[cert.name]?.status;
    if (override) return override;
    return cert.achieved ? "achieved" : "to_be_started";
  };

  const handleCertStatusToggle = (cert) => {
    if (!user) return;
    const current = certStatus(cert);
    const idx = CERT_STATUS_CYCLE.indexOf(current);
    const next = CERT_STATUS_CYCLE[(idx + 1) % CERT_STATUS_CYCLE.length];
    setProofs((prev) => ({
      ...prev,
      [cert.name]: { ...(prev[cert.name] || {}), status: next },
    }));
  };

  const handleCertProofToggle = (certName) => {
    if (openCertProof === certName) {
      setOpenCertProof(null);
    } else {
      setOpenCertProof(certName);
      setCertProofTab("link");
      const existing = proofs[certName];
      setCertProofUrl(existing && existing.type === "link" ? existing.url : "");
    }
  };

  const handleCertSaveLink = (certName) => {
    if (!certProofUrl) return;
    setProofs((prev) => ({
      ...prev,
      [certName]: {
        ...(prev[certName] || {}),
        type: "link",
        url: certProofUrl,
        name: certProofUrl,
      },
    }));
    setOpenCertProof(null);
  };

  const handleCertUploadFile = async (certName, file) => {
    if (!file || !user) return;
    setCertUploading(true);
    try {
      const result = await uploadProofFile(user.uid, certName, file);
      if (result) {
        setProofs((prev) => ({
          ...prev,
          [certName]: {
            ...(prev[certName] || {}),
            type: "file",
            url: result.url,
            name: result.name,
            path: result.path,
          },
        }));
        setOpenCertProof(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[cert proof] upload failed:", err);
    } finally {
      setCertUploading(false);
    }
  };

  const handleCertRemoveProof = (certName) => {
    setProofs((prev) => {
      const next = { ...prev };
      const existing = next[certName];
      if (existing) {
        // Keep status, drop attachment fields
        next[certName] = { status: existing.status };
        if (!next[certName].status) delete next[certName];
      }
      return next;
    });
  };

  const handleNodeClick = (laneIdx, nodeIdx) => {
    if (activeNode && activeNode.laneIdx === laneIdx && activeNode.nodeIdx === nodeIdx) {
      setActiveNode(null);
    } else {
      setActiveNode({ laneIdx, nodeIdx });
    }
  };

  const selectedNode = activeNode ? lanes[activeNode.laneIdx].nodes[activeNode.nodeIdx] : null;
  const selectedLane = activeNode ? lanes[activeNode.laneIdx].name : null;

  return (
    <>
      {/* Hero */}
      <SectionStyled>
        <Container>
          <HeroBadge>CAREER DEVELOPMENT SYSTEM</HeroBadge>
          <HeroTitle>Career Path</HeroTitle>
          <HeroSubtitle>
            From junior to principal. Track where you are, see where you're going.
          </HeroSubtitle>
        </Container>
      </SectionStyled>

      {/* Team Career Paths */}
      <Section>
        <Container>
          <SectionLabel>OUR TEAM</SectionLabel>
          <SectionTitle>Where everyone is on the path</SectionTitle>
          <SectionDesc>
            Career paths aren't straight lines. People jump tracks — a DevOps engineer
            becomes an SRE, an SRE moves to Platform. You keep your seniority and your
            skills. The track changes, the experience stays.
          </SectionDesc>

          <TeamCareerGrid>
            {teamMembers.map((member, i) => (
              <TeamCard key={i}>
                <TeamName>{member.name}</TeamName>
                <TeamRole>{member.role} — {member.track} TRACK</TeamRole>
                <TeamPath>
                  {member.path.map((step, j) => (
                    <React.Fragment key={j}>
                      {j > 0 && <PathArrow>→</PathArrow>}
                      {step.switched ? (
                        <TrackSwitch>{step.track} {step.grade}</TrackSwitch>
                      ) : (
                        <PathNode current={j === member.path.length - 1}>
                          {step.grade}
                        </PathNode>
                      )}
                    </React.Fragment>
                  ))}
                </TeamPath>
                {member.certs.length > 0 && (
                  <TeamCerts>
                    {member.certs.map((c, j) => <MiniCert key={j}>{c}</MiniCert>)}
                  </TeamCerts>
                )}
                {member.note && (
                  <GrowthText style={{ marginTop: "12px", fontSize: "13px", fontStyle: "italic" }}>
                    {member.note}
                  </GrowthText>
                )}
              </TeamCard>
            ))}
          </TeamCareerGrid>
        </Container>
      </Section>

      {/* Career Timeline */}
      <Section>
        <Container>
          <SectionLabel>PROGRESSION</SectionLabel>
          <SectionTitle>Where you are. Where you're going.</SectionTitle>
          <SectionDesc>
            Click any role to see what it takes. Tracks are not silos — you can jump
            from DevOps to SRE at the same level. Your tools change, your judgment doesn't.
          </SectionDesc>

          <TimelineWrapper>
            <LaneContainer>
              {lanes.map((lane, laneIdx) => (
                <Lane key={lane.name}>
                  <LaneName>{lane.name}</LaneName>
                  <LaneNodes>
                    {lane.nodes.map((node, nodeIdx) => (
                      <React.Fragment key={node.title}>
                        {nodeIdx > 0 && <Arrow />}
                        <NodeCard
                          active={activeNode && activeNode.laneIdx === laneIdx && activeNode.nodeIdx === nodeIdx}
                          onClick={() => handleNodeClick(laneIdx, nodeIdx)}
                          aria-label={`View details for ${node.title}`}
                        >
                          <NodeTitle>{node.title}</NodeTitle>
                          <NodeCode>{node.code}</NodeCode>
                        </NodeCard>
                      </React.Fragment>
                    ))}
                  </LaneNodes>
                </Lane>
              ))}
            </LaneContainer>
          </TimelineWrapper>

          {selectedNode && (
            <DetailPanel>
              <DetailLane>{selectedLane} TRACK</DetailLane>
              <DetailTitle>{selectedNode.title}</DetailTitle>
              <DetailGrid>
                <DetailBlock>
                  <DetailBlockLabel>REQUIREMENTS</DetailBlockLabel>
                  <DetailBlockContent>{selectedNode.requirements}</DetailBlockContent>
                </DetailBlock>
                <DetailBlock>
                  <DetailBlockLabel>KEY SKILLS</DetailBlockLabel>
                  <DetailBlockContent>{selectedNode.skills}</DetailBlockContent>
                </DetailBlock>
                <DetailBlock>
                  <DetailBlockLabel>TYPICAL EXPERIENCE</DetailBlockLabel>
                  <DetailBlockContent>{selectedNode.experience}</DetailBlockContent>
                </DetailBlock>
                <DetailBlock>
                  <DetailBlockLabel>SALARY RANGE</DetailBlockLabel>
                  <DetailBlockContent>{selectedNode.salary}</DetailBlockContent>
                </DetailBlock>
              </DetailGrid>
            </DetailPanel>
          )}
        </Container>
      </Section>

      {/* Certifications */}
      <Section>
        <Container>
          <SectionLabel>CERTIFICATIONS</SectionLabel>
          <SectionTitle>Proof of work</SectionTitle>
          <SectionDesc>
            Certifications don't make you good at your job. But they prove you sat down
            and studied the fundamentals. Here's what we track.
          </SectionDesc>

          <MentorshipCallout>
            <MentorshipLabel>MENTORSHIP GROUPS</MentorshipLabel>
            <MentorshipText>
              Not sure which cert to chase first? We run small mentorship groups that work
              through these certifications together — weekly study sessions, exam prep,
              and a senior to debug your blockers.
            </MentorshipText>
          </MentorshipCallout>

          {certifications.map((cert, i) => {
            const status = certStatus(cert);
            const proof = proofs[cert.name];
            const hasAttachment = proof && (proof.type === "link" || proof.type === "file");
            const open = openCertProof === cert.name;
            return (
              <CertCard key={i}>
                <CertRow>
                  <div>
                    <CertName>
                      {cert.name}
                      {hasAttachment && <ProofBadge>· proof attached</ProofBadge>}
                    </CertName>
                    <CertIssuer>{cert.issuer}</CertIssuer>
                  </div>
                  <CertActions>
                    <CertStatus
                      type="button"
                      status={status}
                      onClick={() => handleCertStatusToggle(cert)}
                      disabled={!user}
                      title={user ? "Click to cycle status" : "Sign in to change status"}
                    >
                      {CERT_STATUS_LABEL[status]}
                    </CertStatus>
                    <ProofToggle
                      type="button"
                      onClick={() => handleCertProofToggle(cert.name)}
                    >
                      {hasAttachment ? "Edit proof" : "Add proof"}
                    </ProofToggle>
                  </CertActions>
                </CertRow>

                <ProofPanel open={open}>
                  {!user ? (
                    <ProofRow>
                      <span style={{
                        fontFamily: "'Space Mono', monospace",
                        fontSize: 11,
                        letterSpacing: "0.06em",
                        color: "var(--nd-text-secondary)",
                      }}>
                        Sign in to attach proof
                      </span>
                      <SignInProofCTA type="button" onClick={signIn}>
                        Sign in with Google
                      </SignInProofCTA>
                    </ProofRow>
                  ) : (
                    <>
                      <ProofTabs>
                        <ProofTab type="button" active={certProofTab === "link"} onClick={() => setCertProofTab("link")}>Link</ProofTab>
                        <ProofTab type="button" active={certProofTab === "file"} onClick={() => setCertProofTab("file")}>File</ProofTab>
                      </ProofTabs>
                      {certProofTab === "link" ? (
                        <ProofRow>
                          <ProofInput
                            type="url"
                            placeholder="https://… certification or repo URL"
                            value={certProofUrl}
                            onChange={(e) => setCertProofUrl(e.target.value)}
                          />
                          <ProofSaveBtn
                            type="button"
                            disabled={!certProofUrl}
                            onClick={() => handleCertSaveLink(cert.name)}
                          >
                            Save
                          </ProofSaveBtn>
                        </ProofRow>
                      ) : (
                        <ProofRow>
                          <input
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg,.webp"
                            style={{
                              fontFamily: "'Space Mono', monospace",
                              fontSize: 12,
                              color: "var(--nd-text-secondary)",
                            }}
                            onChange={(e) => handleCertUploadFile(cert.name, e.target.files[0])}
                          />
                          {certUploading && (
                            <span style={{
                              fontFamily: "'Space Mono', monospace",
                              fontSize: 10,
                              letterSpacing: "0.08em",
                              color: "var(--nd-text-disabled)",
                            }}>
                              Uploading…
                            </span>
                          )}
                        </ProofRow>
                      )}
                      {hasAttachment && (
                        <ProofRow style={{ marginTop: 12 }}>
                          <ProofExisting href={proof.url} target="_blank" rel="noopener noreferrer">
                            ↗ {proof.name}
                          </ProofExisting>
                          <ProofSaveBtn
                            type="button"
                            onClick={() => handleCertRemoveProof(cert.name)}
                            style={{ background: "transparent", color: "var(--nd-accent)", border: "1px solid var(--nd-accent)" }}
                          >
                            Remove
                          </ProofSaveBtn>
                        </ProofRow>
                      )}
                    </>
                  )}
                </ProofPanel>
              </CertCard>
            );
          })}
        </Container>
      </Section>

      {/* Skills Inventory */}
      <Section>
        <Container>
          <SectionLabel>SELF-ASSESSMENT</SectionLabel>
          <SectionTitle>Skills inventory</SectionTitle>
          <SectionDesc>
            Be honest with yourself. "Competent" means you can do it without Googling
            every step. "Expert" means other people ask you how.
          </SectionDesc>

          <AuthBar>
            {user ? (
              <>
                <AuthLabel>Signed in as {user.email}{saveMsg && <SaveStatus>{saveMsg}</SaveStatus>}</AuthLabel>
                <AuthButton onClick={signOut}>SIGN OUT</AuthButton>
              </>
            ) : (
              <>
                <AuthLabel>Sign in to save your progress to the cloud</AuthLabel>
                <AuthButton onClick={signIn}>SIGN IN WITH GOOGLE</AuthButton>
              </>
            )}
          </AuthBar>

          {user && (
            <AuthBar>
              <AuthLabel>
                Profile visibility: {profilePublic ? "PUBLIC" : "PRIVATE"}
                {copyHint && <SaveStatus>{copyHint}</SaveStatus>}
              </AuthLabel>
              <AuthButton onClick={handleTogglePublic}>
                {profilePublic ? "MAKE PRIVATE" : "MAKE PUBLIC"}
              </AuthButton>
              {profilePublic && (
                <AuthButton onClick={handleCopyShareLink}>COPY SHARE LINK</AuthButton>
              )}
              <AuthLabel>Email notifications: {emailMuted ? "OFF" : "ON"}</AuthLabel>
              <AuthButton onClick={handleToggleEmailMute}>
                {emailMuted ? "TURN ON" : "MUTE ALL"}
              </AuthButton>
            </AuthBar>
          )}

          <div>
            {skillsInventory.map((skill) => {
              const cert = certs[skill];
              const proofOpen = openProof === skill;
              const currentLevel = skills[skill];
              const achieved = currentLevel === "Competent" || currentLevel === "Expert";
              const canProof = achieved || Boolean(cert);
              return (
                <SkillRow key={skill}>
                  <SkillTopRow>
                    <SkillName>
                      {skill}
                      {cert && <ProofBadge>· proof attached</ProofBadge>}
                    </SkillName>
                    <SkillRight>
                      <LevelButtons>
                        {levels.map((level) => (
                          <LevelBtn
                            key={level}
                            active={currentLevel === level}
                            onClick={() => setSkills(prev => ({ ...prev, [skill]: level }))}
                            aria-label={`${skill}: ${level}`}
                          >
                            {level}
                          </LevelBtn>
                        ))}
                      </LevelButtons>
                      {canProof && (
                        <ProofToggle
                          type="button"
                          onClick={() => handleProofToggle(skill)}
                          aria-label={`Proof of work for ${skill}`}
                        >
                          {cert ? "Edit proof" : "Add proof"}
                        </ProofToggle>
                      )}
                    </SkillRight>
                  </SkillTopRow>

                  <ProofPanel open={proofOpen}>
                    {!user ? (
                      <ProofRow>
                        <span style={{
                          fontFamily: "'Space Mono', monospace",
                          fontSize: 11,
                          letterSpacing: "0.06em",
                          color: "var(--nd-text-secondary)",
                        }}>
                          Sign in to attach proof
                        </span>
                        <SignInProofCTA type="button" onClick={signIn}>
                          Sign in with Google
                        </SignInProofCTA>
                      </ProofRow>
                    ) : (
                      <>
                        <ProofTabs>
                          <ProofTab type="button" active={proofTab === "link"} onClick={() => setProofTab("link")}>
                            Link
                          </ProofTab>
                          <ProofTab type="button" active={proofTab === "file"} onClick={() => setProofTab("file")}>
                            File
                          </ProofTab>
                        </ProofTabs>
                        {proofTab === "link" ? (
                          <ProofRow>
                            <ProofInput
                              type="url"
                              placeholder="https://… certification or repo URL"
                              value={proofUrl}
                              onChange={(e) => setProofUrl(e.target.value)}
                            />
                            <ProofSaveBtn
                              type="button"
                              disabled={!proofUrl}
                              onClick={() => handleSaveLink(skill)}
                            >
                              Save
                            </ProofSaveBtn>
                          </ProofRow>
                        ) : (
                          <ProofRow>
                            <input
                              type="file"
                              accept=".pdf,.png,.jpg,.jpeg,.webp"
                              style={{
                                fontFamily: "'Space Mono', monospace",
                                fontSize: 12,
                                color: "var(--nd-text-secondary)",
                              }}
                              onChange={(e) => handleUploadFile(skill, e.target.files[0])}
                            />
                            {uploading && (
                              <span style={{
                                fontFamily: "'Space Mono', monospace",
                                fontSize: 10,
                                letterSpacing: "0.08em",
                                color: "var(--nd-text-disabled)",
                              }}>
                                Uploading…
                              </span>
                            )}
                          </ProofRow>
                        )}
                        {cert && (
                          <ProofRow style={{ marginTop: 12 }}>
                            <ProofExisting
                              href={cert.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {cert.type === "file" ? "↗ " : "↗ "}
                              {cert.name}
                            </ProofExisting>
                            <ProofSaveBtn
                              type="button"
                              onClick={() => handleRemoveCert(skill)}
                              style={{ background: "transparent", color: "var(--nd-accent)", border: "1px solid var(--nd-accent)" }}
                            >
                              Remove
                            </ProofSaveBtn>
                          </ProofRow>
                        )}
                      </>
                    )}
                  </ProofPanel>
                </SkillRow>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* Role Jumping + Growth Philosophy */}
      <Section>
        <Container>
          <GrowthBox>
            <SectionLabel>PHILOSOPHY</SectionLabel>
            <GrowthTitle>Tracks aren't cages</GrowthTitle>
            <GrowthText>
              Your title belongs to you, not to a track. A Senior DevOps engineer who
              moves to SRE stays senior — architecture, incident response, and
              operational judgment carry across.
            </GrowthText>
            <GrowthText>
              Progress reviews are quarterly, with notes, action items, and
              follow-through — not an annual ritual no one remembers.
            </GrowthText>
            <GrowthText>
              <Honest>
                The best thing we can do is help you outgrow yourself.
              </Honest>
            </GrowthText>
          </GrowthBox>
        </Container>
      </Section>
    </>
  );
};

export default CareerPath;
